import ChevronRight from "@mui/icons-material/ChevronRight";
import { Button, Skeleton, ButtonProps, Tab, Tabs, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useDocumentReview } from "providers/documentReview";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DocumentReviewStatus, getDocumentReviewStatusLabel } from "util/documentReviewStatus";

import { DocumentDialogContainer } from "../shared";
import { CreatePermitRenewalTaskToggle } from "./components/CreatePermitRenewalTaskToggle";
import { DocumentDetailsSection } from "./components/DocumentDetailsSection";
import { DocumentMetadataFormSectionsSkeleton } from "./components/DocumentMetadataFormSectionsSkeleton";
import { DocumentSwitcherSelect } from "./components/DocumentSwitcherSelect";
import { RelatedTasksSection, RelatedTasksTabHeader } from "./components/RelatedTasksSection";
import { TabPanel } from "./components/TabPanel";
import { useCreatePermitRenewalOnSave } from "./hooks/useCreatePermitRenewalOnSave";
import { useDocumentDetailsForm } from "./hooks/useDocumentDetailsForm";
import { useDocumentMetadata } from "./hooks/useDocumentMetadata";
import {
  REVIEW_TABS,
  ReviewDocumentMetadataDialogProps,
  ReviewTab,
  ReviewTabValue,
  TabSectionHandle,
} from "./types";

export function ReviewDocumentMetadataDialog({
  open,
  onClose,
  documentIds,
  tenantId,
  onComplete,
  title,
  hasDocumentAI = true,
  onCurrentDocumentChange,
}: ReviewDocumentMetadataDialogProps) {
  const theme = useTheme();
  const alerts = useAlerts();
  const [currentDocumentId, setCurrentDocumentId] = useState<string | undefined>(
    () => documentIds[0]
  );
  const [currentTab, setCurrentTab] = useState<ReviewTabValue>("document-details");

  const [confirmSkipDialogOpen, setConfirmSkipDialogOpen] = useState(false);
  const openConfirmSkipDialog = useCallback(() => setConfirmSkipDialogOpen(true), []);
  const closeConfirmSkipDialog = useCallback(() => setConfirmSkipDialogOpen(false), []);

  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState(false);
  const openConfirmCloseDialog = useCallback(() => setConfirmCloseDialogOpen(true), []);
  const closeConfirmCloseDialog = useCallback(() => setConfirmCloseDialogOpen(false), []);

  const currentIndex = useMemo(() => {
    if (!currentDocumentId) {
      return 0;
    }
    const index = documentIds.indexOf(currentDocumentId);
    return index >= 0 ? index : 0;
  }, [currentDocumentId, documentIds]);

  const hasNext = currentIndex < documentIds.length - 1;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const onDocumentChange = useCallback(
    (documentId: string) => {
      onCurrentDocumentChange?.(documentId);
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    },
    [onCurrentDocumentChange]
  );

  useEffect(() => {
    if (documentIds.length === 0) {
      setCurrentDocumentId(undefined);
      return;
    }
    setCurrentDocumentId((prev) => {
      if (prev && documentIds.includes(prev)) return prev;
      return documentIds[0];
    });
  }, [documentIds]);

  const navigateToDocument = useCallback(
    (documentId: string) => {
      setCurrentTab(REVIEW_TABS.DOCUMENT_DETAILS);
      setCurrentDocumentId(documentId);
      onDocumentChange(documentId);
    },
    [onDocumentChange]
  );

  const handleSelectDocument = useCallback(
    (documentId: string) => {
      if (!documentId || documentId === currentDocumentId) return;
      navigateToDocument(documentId);
    },
    [currentDocumentId, navigateToDocument]
  );

  const handleTabChange = useCallback((_: React.SyntheticEvent, value: ReviewTabValue) => {
    setCurrentTab(value);
  }, []);

  const {
    document,
    metadata,
    loading,
    suggestedFacilities,
    suggestedFacilitiesLoading,
    previewUrl,
  } = useDocumentMetadata({
    documentId: currentDocumentId,
    open,
    hasDocumentAI,
  });

  const { carryForwardDocumentIds } = useDocumentReview();
  const form = useDocumentDetailsForm({
    documentId: currentDocumentId,
    document,
    metadata,
    tenantId,
    carryForwardByDefault:
      currentDocumentId && carryForwardDocumentIds.has(currentDocumentId) ? true : undefined,
  });

  const watchedDocumentFacilities = form.methods.watch("facilities");
  const watchedDocumentDueDate = form.methods.watch("dueDate");
  const watchedDocumentExpirationDate = form.methods.watch("expirationDate");
  const watchedDocumentType = form.methods.watch("documentType");

  const needsReview = !!document?.documentMetadata?.needsReview;

  const hasRelatedTasks = (document?.tasks ?? []).length > 0;

  const {
    showToggle: showCreatePermitRenewalToggle,
    canCreate: canCreatePermitRenewal,
    tooltip: permitRenewalTooltip,
    createPermitRenewalTask,
    setCreatePermitRenewalTask,
    maybeGenerateOnSave,
    generatePermitRenewalsLoading,
  } = useCreatePermitRenewalOnSave({
    documentId: currentDocumentId,
    hasRelatedTasks,
    // Prefer live form type; fall back to loaded document while the form is resetting.
    documentType: watchedDocumentType ?? document?.documentType,
    dueDate: watchedDocumentDueDate,
    expirationDate: watchedDocumentExpirationDate,
    extractionStatus: document?.extractionStatus,
    tenantId: document?.tenantId ?? tenantId,
    tasks: document?.tasks,
  });

  const tabs = useMemo((): ReviewTab[] => {
    const result: ReviewTab[] = [
      { value: REVIEW_TABS.DOCUMENT_DETAILS, label: "Document Details", showPreview: true },
    ];

    if (hasRelatedTasks) {
      result.push({
        value: REVIEW_TABS.RELATED_TASKS,
        label: <RelatedTasksTabHeader document={document} />,
        showPreview: false,
      });
    }

    return result;
  }, [document, hasRelatedTasks]);

  const showTabs = tabs.length > 1;
  const activeTab = tabs.find((t) => t.value === currentTab) ?? tabs[0];
  const effectiveTab = activeTab?.value ?? REVIEW_TABS.DOCUMENT_DETAILS;
  const showPreview = activeTab?.showPreview ?? true;

  const isSaving = loading || form.saving || generatePermitRenewalsLoading;

  const detailsRef = useRef<TabSectionHandle>(null);

  const focusDueDateField = useCallback(() => {
    if (effectiveTab !== REVIEW_TABS.DOCUMENT_DETAILS) {
      setCurrentTab(REVIEW_TABS.DOCUMENT_DETAILS);
    }

    // Defer until the details tab (and its fields) are mounted/visible.
    window.requestAnimationFrame(() => {
      form.methods.setFocus("dueDate");

      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }

      const dueDateLabel = Array.from(container.querySelectorAll("label")).find((label) =>
        label.textContent?.includes("Due Date")
      );
      const fieldRoot = dueDateLabel?.closest(".MuiFormControl-root") ?? dueDateLabel;
      fieldRoot?.scrollIntoView({ block: "center", behavior: "smooth" });

      const input = fieldRoot?.querySelector<HTMLElement>(
        "input:not([type='hidden']), textarea, [role='combobox']"
      );
      input?.focus({ preventScroll: true });
    });
  }, [effectiveTab, form.methods]);

  const handleSaveAndNext = useCallback(async () => {
    try {
      if (!currentDocumentId) {
        alerts.error("Document not selected");
        return;
      }

      let didSave = false;

      // Save document details (validates form, then saves)
      if (detailsRef.current) {
        const saved = await detailsRef.current.save();
        if (saved) didSave = true;
      }

      if (didSave) {
        alerts.success("Document and metadata saved successfully");
      }

      await maybeGenerateOnSave();

      if (hasNext) {
        navigateToDocument(documentIds[currentIndex + 1]);
      } else {
        onComplete?.();
        onClose();
      }
    } catch (error) {
      alerts.error("Failed to save document and metadata", error);
    }
  }, [
    currentDocumentId,
    currentIndex,
    hasNext,
    documentIds,
    alerts,
    onComplete,
    onClose,
    navigateToDocument,
    maybeGenerateOnSave,
  ]);

  const handleSkipWithoutReviewing = useCallback(() => {
    if (hasNext) {
      navigateToDocument(documentIds[currentIndex + 1]);
      closeConfirmSkipDialog();
    }
  }, [hasNext, currentIndex, documentIds, navigateToDocument, closeConfirmSkipDialog]);

  const dialogTitle = useMemo(() => {
    if (title) return title;

    if (hasDocumentAI && metadata?.needsReview) {
      return "Review & Confirm Details";
    }
    return "Edit Document";
  }, [title, hasDocumentAI, metadata?.needsReview]);

  const handleClose = useMemo(
    () => (needsReview ? openConfirmCloseDialog : onClose),
    [needsReview, openConfirmCloseDialog, onClose]
  );

  if (!open) return null;

  return (
    <>
      <DocumentDialogContainer
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={"lg"}
        title={dialogTitle}
        sx={{
          "& .MuiDialog-paper": {
            height: "960px", // fixed height so the dialog can scroll internally
          },
        }}
        contentSx={{ pt: 0 }}
        stickyHeaderContent={
          showTabs && !loading && document ? (
            <Tabs value={effectiveTab} onChange={handleTabChange} sx={{ mb: 1 }}>
              {tabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          ) : undefined
        }
        formContainerProps={{
          ref: scrollContainerRef,
        }}
        previewUrl={showPreview ? previewUrl : undefined}
        fileExtension={document?.fileExtension ?? undefined}
        previewLoading={loading}
        showPreviewBoxIfFailed={showPreview}
        formColumnSize={{ xs: showPreview ? 6 : 12 }}
        previewColumnSize={{ xs: showPreview ? 6 : 0 }}
        formContainerSx={{ maxHeight: "calc(960px - 140px)" }}
        secondaryActionElements={[
          <ButtonWithSkeleton
            key="close"
            onClick={handleClose}
            disabled={loading}
            loading={loading}
            size="medium"
            variant="text"
            sx={{ color: theme.palette.text.secondary }}
          >
            {needsReview ? "Close Without Reviewing" : "Close"}
          </ButtonWithSkeleton>,
        ]}
        additionalPrimaryActionElements={[
          ...(showCreatePermitRenewalToggle
            ? [
                <CreatePermitRenewalTaskToggle
                  key="create-permit-renewal-toggle"
                  checked={createPermitRenewalTask}
                  disabled={!canCreatePermitRenewal || isSaving}
                  loading={loading}
                  tooltip={permitRenewalTooltip}
                  onChange={setCreatePermitRenewalTask}
                  onJumpToDueDate={focusDueDateField}
                />,
              ]
            : []),
          ...(documentIds.length > 1 && hasNext
            ? [
                <ButtonWithSkeleton
                  key="skip-without-reviewing"
                  onClick={openConfirmSkipDialog}
                  disabled={!hasNext || loading}
                  loading={loading}
                  size="medium"
                  variant="text"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Skip
                </ButtonWithSkeleton>,
              ]
            : []),
        ]}
        primaryActionButton={
          <ButtonWithSkeleton
            key="primary-action-button"
            loading={isSaving}
            disabled={isSaving}
            variant="contained"
            onClick={handleSaveAndNext}
            endIcon={hasNext ? <ChevronRight /> : undefined}
          >
            {getPrimaryButtonLabel(needsReview, hasNext)}
          </ButtonWithSkeleton>
        }
        noPrimaryAction={false}
        hideCancelButton={true}
        titleBarActionElement={
          documentIds.length > 1 ? (
            <DocumentSwitcherSelect
              documentIds={documentIds}
              tenantId={tenantId}
              selectedId={currentDocumentId}
              skip={!open}
              disabled={documentIds.length <= 1}
              onSelect={handleSelectDocument}
            />
          ) : null
        }
      >
        <>
          {loading || !document ? (
            <DocumentMetadataFormSectionsSkeleton />
          ) : (
            <>
              <TabPanel active={effectiveTab === REVIEW_TABS.DOCUMENT_DETAILS}>
                <DocumentDetailsSection
                  ref={detailsRef}
                  form={form}
                  document={document}
                  suggestedFacilities={suggestedFacilities}
                  suggestedFacilitiesLoading={suggestedFacilitiesLoading}
                  tenantId={tenantId}
                />
              </TabPanel>

              <TabPanel active={effectiveTab === REVIEW_TABS.RELATED_TASKS}>
                <RelatedTasksSection
                  document={document}
                  documentFormFacilities={watchedDocumentFacilities}
                  documentFormDueDate={watchedDocumentDueDate}
                />
              </TabPanel>
            </>
          )}
        </>
      </DocumentDialogContainer>
      <ConfirmDialog
        open={confirmSkipDialogOpen}
        onClose={closeConfirmSkipDialog}
        onConfirm={handleSkipWithoutReviewing}
        header="Are you sure you want to skip review?"
        info={`You can revisit and review any time in the future, but this document will be labeled as ${getDocumentReviewStatusLabel(DocumentReviewStatus.NeedsReview)}.`}
        confirmText="Yes, Next Document"
      />
      <ConfirmDialog
        open={confirmCloseDialogOpen}
        onClose={closeConfirmCloseDialog}
        onConfirm={onClose}
        header={`Are you sure you want to skip reviewing ${documentIds.length > 1 ? "all unreviewed documents" : "this document"}?`}
        info={`You can revisit and review any time in the future, but ${documentIds.length > 1 ? "all unreviewed documents" : "this document"} will be labeled as ${getDocumentReviewStatusLabel(DocumentReviewStatus.NeedsReview)}.`}
        confirmText="Yes, Skip & Close"
      />
    </>
  );
}

function getPrimaryButtonLabel(needsReview: boolean, hasNext: boolean): string {
  if (hasNext) {
    return needsReview ? "Confirm Details & Next" : "Save & Next";
  }
  return needsReview ? "Confirm Details" : "Save";
}

/**
 *  A simple wrapper around the Button component that shows a skeleton when the loading prop is true.
 */
const ButtonWithSkeleton = <T extends ButtonProps>(props: T & { loading: boolean }) => {
  const { loading, key, ...rest } = props;
  if (loading) {
    return <Skeleton key={key} width={100} height={32} />;
  }
  return <Button {...rest} />;
};
